import type { AuthorityRequirement } from "@carepass/auth";

export const ADMIN_ROLE_AUTHORITIES = [
  "ROLE_SUPER",
  "ROLE_OPERATOR",
  "ROLE_CS",
  "ROLE_VIEWER",
] as const;

const ADMIN_ROLE_DISPLAY_LABELS: Record<(typeof ADMIN_ROLE_AUTHORITIES)[number], string> = {
  ROLE_SUPER: "시스템 관리자",
  ROLE_OPERATOR: "운영 담당자",
  ROLE_CS: "고객지원 담당자",
  ROLE_VIEWER: "조회권한",
};

export function getAdminRoleLabels(authorities: readonly string[]): string[] {
  return ADMIN_ROLE_AUTHORITIES.filter(role => authorities.includes(role)).map(role => ADMIN_ROLE_DISPLAY_LABELS[role]);
}

export type AdminPageId = `ADM-S${
  | "01" | "02" | "03" | "04" | "05" | "06" | "07" | "08" | "09" | "10" | "11"
  | "12" | "13" | "14" | "15" | "16" | "17" | "18" | "19" | "20" | "21" | "22"
  | "23" | "24" | "25" | "26" | "27" | "28" | "29" | "30" | "31" | "32"
  | "33" | "34" | "35" | "36" | "37" | "38" | "39" | "40" | "41" | "42"}`;

export interface AdminPagePolicy {
  readonly public?: boolean;
  readonly setup?: "password" | "totp";
  readonly authorities?: AuthorityRequirement;
}

const operationPolicy = (authorities: AuthorityRequirement): AdminPagePolicy => ({ authorities });

export const ADMIN_PAGE_POLICIES: Readonly<Record<AdminPageId, AdminPagePolicy>> = {
  "ADM-S01": { public: true },
  "ADM-S02": { setup: "password" },
  "ADM-S03": { setup: "totp", authorities: { any: ["TOTP_SETUP", "ADMIN_MANAGE"] } },
  "ADM-S04": operationPolicy({ all: ["DASHBOARD_READ"] }),
  "ADM-S05": operationPolicy({ all: ["MEMBER_READ"] }),
  "ADM-S06": operationPolicy({ all: ["MEMBER_READ"] }),
  "ADM-S07": operationPolicy({ all: ["TRANSACTION_READ"] }),
  "ADM-S08": operationPolicy({ all: ["TRANSACTION_READ"] }),
  "ADM-S09": operationPolicy({ all: ["TRANSFER_READ"] }),
  "ADM-S10": operationPolicy({ all: ["TRANSFER_READ"] }),
  "ADM-S11": operationPolicy({ any: ["PAYMENT_CANCEL", "TRANSFER_EXECUTE"] }),
  "ADM-S12": operationPolicy({ all: ["BENEFIT_READ"] }),
  "ADM-S13": operationPolicy({ all: ["INQUIRY_MANAGE"] }),
  "ADM-S14": operationPolicy({ all: ["NOTIFICATION_MANAGE"] }),
  "ADM-S15": operationPolicy({ all: ["CONTENT_MANAGE"] }),
  "ADM-S16": operationPolicy({ all: ["PARTNER_MANAGE"] }),
  "ADM-S17": operationPolicy({ all: ["ROLE_SUPER", "PROVIDER_MANAGE"] }),
  "ADM-S18": operationPolicy({ any: ["AUDIT_READ", "PROVIDER_MANAGE"] }),
  "ADM-S19": operationPolicy({ all: ["AUDIT_READ"] }),
  "ADM-S20": operationPolicy({ all: ["DATA_EXPORT"] }),
  "ADM-S21": operationPolicy({ all: ["ROLE_SUPER", "ADMIN_MANAGE"] }),
  "ADM-S22": operationPolicy({ all: ["ROLE_SUPER", "ADMIN_MANAGE"] }),
  "ADM-S23": operationPolicy({ any: ["PAYMENT_CANCEL", "TRANSFER_EXECUTE"] }),
  "ADM-S24": operationPolicy({ all: ["BENEFIT_READ"] }),
  "ADM-S25": operationPolicy({ all: ["INQUIRY_MANAGE"] }),
  "ADM-S26": operationPolicy({ all: ["NOTIFICATION_MANAGE"] }),
  "ADM-S27": operationPolicy({ all: ["CONTENT_MANAGE"] }),
  "ADM-S28": operationPolicy({ all: ["CONTENT_MANAGE"] }),
  "ADM-S29": operationPolicy({ all: ["ROLE_SUPER", "PROVIDER_MANAGE"] }),
  "ADM-S30": operationPolicy({ any: ["AUDIT_READ", "PROVIDER_MANAGE"] }),
  "ADM-S31": operationPolicy({ any: ["AUDIT_READ", "PROVIDER_MANAGE"] }),
  "ADM-S32": operationPolicy({ all: ["DATA_EXPORT"] }),
  "ADM-S33": operationPolicy({ all: ["TRANSACTION_READ"] }),
  "ADM-S34": operationPolicy({ all: ["MEMBER_READ"] }),
  "ADM-S35": operationPolicy({ all: ["AUDIT_READ"] }),
  "ADM-S36": operationPolicy({ all: ["AUDIT_READ"] }),
  "ADM-S37": operationPolicy({ all: ["AUDIT_READ"] }),
  "ADM-S38": operationPolicy({ all: ["AUDIT_READ"] }),
  "ADM-S39": operationPolicy({ all: ["AUDIT_READ"] }),
  "ADM-S41": operationPolicy({ all: ["DATA_EXPORT", "TRANSACTION_READ"] }),
  "ADM-S42": operationPolicy({ any: ["DASHBOARD_READ", "MEMBERSHIP_STATUS_READ"] }),
  "ADM-S40": operationPolicy({ any: ["DATA_EXPORT", "REVENUE_READ"] }),
};

export function isRestrictedViewer(authorities: readonly string[]): boolean {
  return authorities.includes("ROLE_VIEWER") && !["ROLE_SUPER", "ROLE_OPERATOR", "ROLE_CS"].some(role => authorities.includes(role));
}

export function defaultAdminPath(authorities: readonly string[]): string {
  return isRestrictedViewer(authorities) ? "/membership-status" : "/";
}

export function hasAdminRole(authorities: readonly string[]): boolean {
  const granted = new Set(authorities);
  return ADMIN_ROLE_AUTHORITIES.some((role) => granted.has(role));
}

export function meetsAuthorityRequirement(
  authorities: readonly string[],
  requirement: AuthorityRequirement = {},
): boolean {
  const granted = new Set(authorities);
  const all = requirement.all ?? [];
  const any = requirement.any ?? [];
  return all.every((authority) => granted.has(authority))
    && (any.length === 0 || any.some((authority) => granted.has(authority)));
}

export function canAccessAdminPage(
  pageId: AdminPageId,
  authorities: readonly string[],
  phase: "anonymous" | "password-change" | "totp-enrollment" | "operations",
): boolean {
  const policy = ADMIN_PAGE_POLICIES[pageId];
  if (policy.public) return true;
  if (phase === "anonymous") return false;
  if (policy.setup === "password") {
    return phase === "password-change" || (phase === "operations" && hasAdminRole(authorities));
  }
  if (policy.setup === "totp") {
    return (phase === "totp-enrollment" || (phase === "operations" && hasAdminRole(authorities)))
      && meetsAuthorityRequirement(authorities, policy.authorities);
  }
  if (phase === "operations" && isRestrictedViewer(authorities)) {
    return ["ADM-S42", "ADM-S40", "ADM-S41"].includes(pageId);
  }
  if (phase === "operations" && pageId === "ADM-S41" && authorities.includes("PAYMENT_AGENCY_READ")) return hasAdminRole(authorities);
  return phase === "operations"
    && hasAdminRole(authorities)
    && meetsAuthorityRequirement(authorities, policy.authorities);
}
